import { Autocomplete, Paper, TextField } from "@mui/material";
import React, { useState, useEffect } from "react";

/**
 * SingleSelectAutocomplete
 *
 * A Material UI-based autocomplete component for single option selection.
 *
 * Props:
 * - inputLabel (string): Label and placeholder text for the input.
 * - selectedOption (object | null): The currently selected option object.
 * - setSelectedOption (function): Function to update the selected option.
 * - optionsList (array): The complete list of selectable options.
 * - required (boolean): Whether the input is mandatory.
 * - name (string): The key in the option object to display as the label.
 * - size (string): Size of the component (e.g., 'small', 'medium').
 * - onBlur (function): Function triggered on input blur (default: no-op).
 */
export default function SingleSelectAutocomplete({
  inputLabel,
  selectedOption,
  setSelectedOption,
  optionsList,
  required,
  name,
  size,
  onBlur = () => {},
}) {
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(optionsList || []);

  useEffect(() => {
    const availableOptions = optionsList.filter(
      (item) => !selectedOption || selectedOption[name] !== item[name]
    );
    setFilteredOptions(availableOptions);
  }, [selectedOption, optionsList]);

  return (
    <Autocomplete
      value={selectedOption || null}
      onChange={(event, newValue) => setSelectedOption(newValue)}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      className="w-100 autocomplete-mui"
      PaperComponent={({ children }) => (
        <Paper className="autocomplete-paper">{children}</Paper>
      )}
      size={size || "medium"}
      id="single-select-autocomplete"
      options={filteredOptions}
      getOptionLabel={(option) => option[name] || ""}
      renderInput={(params) => (
        <TextField
          {...params}
          label={inputLabel}
          placeholder={inputLabel}
          required={required && !selectedOption}
          autoComplete="off"
        />
      )}
    />
  );
}
